<template>
  <div id="app-content" class="app">
    <Sidebar v-if="useShowComponentsStore().checkRoute()" />
    <SearchModal v-if="gloablSearchOpen" />
    <div class="header" v-if="useShowComponentsStore().checkRoute()">
      <Header />
    </div>
    <div
      id="main-container-views"
      class="d-flex flex-column"
      :class="
        route.meta?.hiddenBreadcrumb || route.name === 'Home'
          ? 'h-auto'
          : 'pt-breadcrumb-nav'
      "
      :style="{ minHeight: minHeight() }"
    >
      <div
        v-if="route.meta.showOverlay"
        :class="`${isMenuOpen ? 'menu-open-overlay' : 'menu-closed-overlay'}`"
        class="overlay-choose-operator"
        @click="openSkinsDropdown"
      >
        <button
          type="button"
          class="choose-operator-card group"
          @click.stop="openSkinsDropdown"
          title="Click to open Skins menu"
        >
          <div class="choose-operator-icon-box">
            <i class="fa-solid fa-layer-group"></i>
          </div>
          <div class="choose-operator-info">
            <span class="choose-operator-main">Choose Skin to proceed</span>
            <span class="choose-operator-hint">Click to select an operator skin</span>
          </div>
          <div class="choose-operator-action">
            <i class="fa-solid fa-chevron-right group-hover:translate-x-0.5 transition-transform duration-200"></i>
          </div>
        </button>
      </div>
      <BreadCrumbs />
      <router-view />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from "vue";
import { useRoute } from "vue-router";
import Sidebar from "./components/Sidebar.vue";
import BreadCrumbs from "./components/BreadCrumbs.vue";
import { useShowComponentsStore } from "./store/showComponents";
import Header from "./components/Header.vue";
import SearchModal from "./components/GlobalSearch/SearchModal.vue";
import { useStore } from "vuex";
import { watch } from "vue";
import searchModalStore from "./components/GlobalSearch/searchStore";
import { fetchAppConfiguration } from "./helpers/appConfiguration";
import { useHeaderNavigation } from "./composables/useHeaderNavigation";

const route = useRoute();
const store = useStore();
const { openMenu } = useHeaderNavigation();

const openSkinsDropdown = () => {
  openMenu("skins");
  const trigger = document.querySelector(".skins-trigger-btn");
  if (trigger) {
    trigger.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }
};

const isMenuOpen = computed(() => store.state.isMenuOpen);
const gloablSearchOpen = computed(
  () => searchModalStore.state.isSearchModalOpen,
);
const selectedDatabase = computed(
  () => store?.getters?.selectedDatabase || "theearcasino",
);

const windowHeight = ref(window.innerHeight);

const minHeight = () => {
  return `${windowHeight.value - 80}px`;
};

const updateWindowHeight = () => {
  windowHeight.value = window.innerHeight;
};

watch(
  () => route.meta?.showOverlay,
  (newVal) => {
    document.body.style.overflow = newVal ? "hidden" : "";
  },
  { immediate: true },
);

onMounted(() => {
  window.addEventListener("resize", updateWindowHeight);
  fetchAppConfiguration(true);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateWindowHeight);
});
</script>

<style lang="scss">
@import "./assets/styles/style.scss";

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  // color: #fff;
  letter-spacing: -0.06em;
}

nav {
  color: #fff;
}

button {
  cursor: pointer;
  appearance: none;
  border: none;
  outline: none;
  background: none;
}

.app {
  .home-page {
    flex: 1 1 0;
    min-width: 0;
    max-width: 100%;
    padding: 2rem;

    &.isMenuOpenPadding {
      padding: 5px 0rem 2rem 250px;

      @media (max-width: 650px) {
        padding: 75px 1rem 2rem 1rem;
      }
    }

    &.isMenuClosePadding {
      padding: 5px 1rem 2rem 4rem;

      @media (max-width: 767px) {
        padding: 1rem 1rem 2rem 1rem;
      }

      @media (max-width: 650px) {
        padding: 75px 1rem 2rem 1rem;
      }
    }
  }
}

.overlay-choose-operator {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: calc(100% - 80px);
  background: rgba(255, 255, 255, 0.45);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  z-index: 75;
  cursor: pointer;

  .choose-operator-card {
    position: absolute;
    top: 48%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    gap: 0.9rem;
    padding: 0.85rem 1.35rem 0.85rem 1rem;
    border-radius: 9999px;
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    border: 1px solid rgba(255, 255, 255, 0.15);
    box-shadow: 0 20px 45px -10px rgba(15, 23, 42, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.08);
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    user-select: none;
    text-align: left;
    max-width: calc(100vw - 32px);
    width: max-content;
    animation: pulse-glow 3s infinite ease-in-out;
    letter-spacing: normal !important;

    &:hover {
      transform: translate(-50%, -50%) scale(1.04);
      background: linear-gradient(135deg, #1e293b 0%, #020617 100%);
      border-color: var(--primary-color, #386f99);
      box-shadow: 0 25px 50px -10px rgba(56, 111, 153, 0.35), 0 0 0 2px var(--primary-color, #386f99);
    }

    &:active {
      transform: translate(-50%, -50%) scale(0.98);
    }

    .choose-operator-icon-box {
      width: 42px;
      height: 42px;
      border-radius: 50%;
      background: var(--primary-color, #386f99);
      color: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      flex-shrink: 0;
      box-shadow: 0 4px 12px rgba(56, 111, 153, 0.4);
      transition: transform 0.25s ease;
    }

    &:hover .choose-operator-icon-box {
      transform: scale(1.08) rotate(5deg);
    }

    .choose-operator-info {
      display: flex;
      flex-direction: column;
      gap: 2px;
      white-space: nowrap;
    }

    .choose-operator-main {
      font-size: 14.5px;
      font-weight: 700;
      color: #ffffff;
      letter-spacing: normal !important;
      line-height: 1.25;
      white-space: nowrap;
    }

    .choose-operator-hint {
      font-size: 11px;
      font-weight: 500;
      color: #94a3b8;
      letter-spacing: normal !important;
      line-height: 1.2;
      white-space: nowrap;
    }

    .choose-operator-action {
      width: 30px;
      height: 30px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.08);
      color: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 11px;
      flex-shrink: 0;
      margin-left: 0.25rem;
      border: 1px solid rgba(255, 255, 255, 0.08);
      transition: all 0.2s ease;
    }

    &:hover .choose-operator-action {
      background: var(--primary-color, #386f99);
      border-color: var(--primary-color, #386f99);
      color: #ffffff;
    }

    @media (max-width: 640px) {
      padding: 0.6rem 0.95rem 0.6rem 0.7rem;
      gap: 0.65rem;
      max-width: calc(100vw - 24px);

      .choose-operator-icon-box {
        width: 36px;
        height: 36px;
        font-size: 14px;
      }

      .choose-operator-main {
        font-size: 13px;
      }

      .choose-operator-hint {
        font-size: 10px;
      }

      .choose-operator-action {
        width: 26px;
        height: 26px;
        font-size: 10px;
        margin-left: 0;
      }
    }
  }
}

@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 20px 45px -10px rgba(15, 23, 42, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.08);
  }
  50% {
    box-shadow: 0 22px 50px -8px rgba(56, 111, 153, 0.3), 0 0 0 2px rgba(56, 111, 153, 0.4);
  }
}

.menu-open-overlay {
  margin-left: 250px;
  width: calc(100% - 250px);
  transition: 0.3s all ease;

  @media (max-width: 767px) {
    margin-left: 0px;
    width: 100%;
  }
}

.menu-closed-overlay {
  margin-left: 64px;
  transition: 0.3s all ease;
  width: calc(100% - 64px);

  @media (max-width: 767px) {
    margin-left: 0px;
    width: 100%;
  }
}
</style>
